/**
 * useAuthors - React Query hook for fetching author/contact list
 *
 * Used in both filter dropdowns and recipient selection
 */

import { useQuery } from '@tanstack/react-query';
import { getClientUserList } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';
import { formatContactList } from 'utils/utils';

/**
 * @param {Object} options
 * @param {string} options.caseId
 * @param {boolean} options.isGlobal
 * @param {boolean} options.enabled
 * @returns {{ authorsList, clientsList, isLoading }}
 */
export function useAuthors({ caseId = '', isGlobal = false, enabled = true } = {}) {
  const query = useQuery({
    queryKey: messageQueryKeys.authors.byCaseId(caseId),
    queryFn: () =>
      getClientUserList({
        caseId: isGlobal ? '' : caseId,
        is_contact_list: false,
        is_team_member_list: false,
        all_contacts: false,
        contact_id: false,
        is_lead: false,
        is_mandatory_lawft_access: true,
        is_active: false,
        excludeFromLoading: true,
        is_include_groups: true,
        active_groups: true,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    select: (response) => {
      const rawList = response?.data || [];
      const formattedList = formatContactList(rawList);

      // Sort users and groups alphabetically
      const sortedList = formattedList?.map((item) => {
        if (item?.code === 'Users') {
          return {
            ...item,
            items: item.items.sort((a, b) => {
              const nameA = (a?.first_name || a?.assignee_name || '').toLowerCase();
              const nameB = (b?.first_name || b?.assignee_name || '').toLowerCase();
              return nameA.localeCompare(nameB);
            }),
          };
        } else if (item?.code === 'Groups') {
          return {
            ...item,
            items: item.items.sort((a, b) => (a.group_name?.toLowerCase() || '').localeCompare(b.group_name?.toLowerCase() || '')),
          };
        }
        return item;
      });

      return {
        authorsList: sortedList || [],
        clientsList: rawList,
      };
    },
  });

  return {
    authorsList: query.data?.authorsList || [],
    clientsList: query.data?.clientsList || [],
    isLoading: query.isLoading,
  };
}
